import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import ClientInfoModal from "../client/ClientInfoModal";
import DateConverter from "../../utils/DateConverter";
import {
  PrimaryColor,
  TableBodyGrey,
  TableHeaderGrey,
  TableRowHover,
} from "../../shared/styles/colors";
import { Spinner } from "../index";
import { Card, CardContent, Typography, Button } from "@material-ui/core";
import { TablePagination } from "@mui/material";
import { ToastContainer } from "react-toastify";
import DB_Status from "../../model/enum/status.class";
import Message from "../../model/message";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { DatabaseEntities } from "../../model/enum/database-entities.enum";
import ApiService from "../../service/ApiService";
import ContactDetailModal from "./ContactDetailModal";
import CachedIcon from "@mui/icons-material/Cached";
import Pathnames from "../../model/enum/Pathnames";
import PageResultResponse from "../../model/page-result-response";
import useStyles from "../styles/cardStyles";
import Misc from "../../utils/Misc";

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: TableHeaderGrey,
    color: PrimaryColor,
    fontWeight: 700,
    whiteSpace: "nowrap",
    zIndex: 0,
    position: "static",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: TableBodyGrey,
    fontFamily: "monospace",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: TableRowHover,
  },
}));

const Contacts = () => {
  const classes = useStyles();
  const [contacts, setContacts] = useState<Message[]>([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("page", page.toString());
    url.searchParams.set("rowsPerPage", rowsPerPage.toString());
    window.history.pushState({}, Pathnames.CONTACT, url);
  });

  //Get all contacts from the database
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const queryParams = new URLSearchParams();
      queryParams.append("page", page.toString());
      queryParams.append("rowsPerPage", rowsPerPage.toString());

      await ApiService.get<PageResultResponse>(
        `${DatabaseEntities.MESSAGES}?${queryParams.toString()}`,
      ).then((data: PageResultResponse) => {
        if (Misc.isArrayEmpty(data.items)) return;
        const { items, count } = data;
        setContacts(items);
        setTotalPages(count);
      });
      setLoading(false);
    };

    if (loading && Misc.isArrayEmpty(contacts)) fetchData();
  }, [contacts]);

  const resetList = (): void => {
    setPage(1);
    setRowsPerPage(10);
    setContacts([]);
    setLoading(true);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown> | null,
    pg: number,
  ) => {
    setPage(pg + 1);
    scrollToTop();
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    let row = parseInt(event.target.value);
    setRowsPerPage(row);
    scrollToTop();
  };

  const scrollToTop = () => {
    setContacts([]);
    setLoading(true);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  return (
    <>
      <Card raised className={classes.card}>
        <div className={classes.headerContainer}>
          <Typography className={classes.title}>Contact Messages</Typography>
          <Button onClick={resetList}>
            <CachedIcon />
          </Button>
        </div>
        <CardContent>
          <Paper>
            <TableContainer>
              <Table
                stickyHeader
                aria-label="customized table"
                padding="normal"
                size="medium"
              >
                <TableHead>
                  <TableRow>
                    <StyledTableCell align="center">
                      <MoreVertIcon />
                    </StyledTableCell>
                    <StyledTableCell align="right">Client</StyledTableCell>
                    <StyledTableCell align="right">Message</StyledTableCell>
                    <StyledTableCell align="right">Status</StyledTableCell>
                    <StyledTableCell align="right">Created At</StyledTableCell>
                  </TableRow>
                </TableHead>
                {loading ? (
                  <Spinner />
                ) : (
                  <TableBody>
                    {contacts.length > 0 ? (
                      contacts.map((row: Message) => (
                        <StyledTableRow key={row.id}>
                          <StyledTableCell align="center">
                            <ContactDetailModal
                              contact={row}
                              resetList={resetList}
                            />
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            <ClientInfoModal clientData={row.Client} />
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            <span
                              className={classes.limitedText}
                              style={{ width: 600 }}
                            >
                              {row.text}
                            </span>
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            <Typography
                              style={DB_Status.mapStatusColor(row.Status?.name)}
                            >
                              {" "}
                              <div
                                style={DB_Status.mapStatusRoundedColor(
                                  row.Status?.name,
                                )}
                              ></div>
                              {row.Status?.name}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {DateConverter.toFullDateAndHours(row.createdAt)}
                          </StyledTableCell>
                        </StyledTableRow>
                      ))
                    ) : (
                      <StyledTableCell align="right">No items</StyledTableCell>
                    )}
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Paper>
          <TablePagination
            component="div"
            count={totalPages}
            page={page - 1}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>
      <ToastContainer />
    </>
  );
};

export default Contacts;
